import dayjs from 'dayjs';

/** Sums one or more numeric fields per calendar month (oldest → newest), for line/bar trend
 * charts. Rows with a null/invalid date field are skipped. Returns one point per month that
 * actually has at least one row — no zero-filled gaps. */
export function groupSumByMonth<T>(
  rows: T[],
  dateField: (row: T) => string | null | undefined,
  valueFields: { key: string; value: (row: T) => number }[],
): { month: string; values: Record<string, number> }[] {
  const buckets = new Map<string, { sortKey: string; month: string; values: Record<string, number> }>();

  for (const row of rows) {
    const raw = dateField(row);
    if (!raw) continue;
    const d = dayjs(raw);
    if (!d.isValid()) continue;

    const sortKey = d.format('YYYY-MM');
    const month = d.format('MMM YYYY');
    let bucket = buckets.get(sortKey);
    if (!bucket) {
      bucket = { sortKey, month, values: Object.fromEntries(valueFields.map((f) => [f.key, 0])) };
      buckets.set(sortKey, bucket);
    }
    for (const f of valueFields) {
      bucket.values[f.key] += f.value(row);
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ month, values }) => ({ month, values }));
}

/** Rows whose date field falls within the last `days` days, inclusive of today — a rolling window,
 * not calendar-day-aligned the way filterLastNMonths is calendar-month-aligned. */
export function filterLastNDays<T>(rows: T[], dateField: (row: T) => string | null | undefined, days: number): T[] {
  const cutoff = dayjs().subtract(days - 1, 'day').startOf('day');
  return rows.filter((row) => {
    const raw = dateField(row);
    if (!raw) return false;
    const d = dayjs(raw);
    return d.isValid() && !d.isBefore(cutoff);
  });
}

/** Rows whose date field falls within the last `months` calendar months, inclusive of the current
 * one — filter with this before groupSumByMonth to get a rolling trailing-N-months trend instead
 * of the full history. */
export function filterLastNMonths<T>(rows: T[], dateField: (row: T) => string | null | undefined, months: number): T[] {
  const cutoff = dayjs().subtract(months - 1, 'month').startOf('month');
  return rows.filter((row) => {
    const raw = dateField(row);
    if (!raw) return false;
    const d = dayjs(raw);
    return d.isValid() && !d.isBefore(cutoff);
  });
}

/** Sums one or more numeric fields per calendar day over a fixed trailing window (today back
 * `days`-1 days), zero-filled — every day in the window gets a point even with no rows, so a
 * short recent-activity chart (e.g. "last 15 days") reads as a continuous timeline instead of
 * skipping quiet days the way groupSumByMonth's sparse (only-months-with-data) output does. */
export function groupSumByDay<T>(
  rows: T[],
  dateField: (row: T) => string | null | undefined,
  valueFields: { key: string; value: (row: T) => number }[],
  days: number,
): { day: string; values: Record<string, number> }[] {
  const today = dayjs().startOf('day');
  const buckets = new Map<string, { sortKey: string; day: string; values: Record<string, number> }>();
  for (let i = days - 1; i >= 0; i--) {
    const d = today.subtract(i, 'day');
    const sortKey = d.format('YYYY-MM-DD');
    buckets.set(sortKey, { sortKey, day: d.format('DD MMM'), values: Object.fromEntries(valueFields.map((f) => [f.key, 0])) });
  }

  for (const row of rows) {
    const raw = dateField(row);
    if (!raw) continue;
    const d = dayjs(raw);
    if (!d.isValid()) continue;
    const bucket = buckets.get(d.format('YYYY-MM-DD'));
    if (!bucket) continue; // outside the trailing window
    for (const f of valueFields) {
      bucket.values[f.key] += f.value(row);
    }
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ day, values }) => ({ day, values }));
}

/** Counts rows per distinct key value (e.g. status), descending by count. */
export function groupCountBy<T>(rows: T[], keyField: (row: T) => string | null | undefined): { key: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = keyField(row) ?? 'Unknown';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

/** Sums a numeric field per distinct key value, descending by sum. */
export function groupSumBy<T>(
  rows: T[],
  keyField: (row: T) => string | null | undefined,
  valueField: (row: T) => number,
): { key: string; total: number }[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = keyField(row) ?? 'Unknown';
    totals.set(key, (totals.get(key) ?? 0) + valueField(row));
  }
  return Array.from(totals.entries())
    .map(([key, total]) => ({ key, total }))
    .sort((a, b) => b.total - a.total);
}

/** Top N rows by a numeric field, descending. */
export function topN<T>(rows: T[], valueField: (row: T) => number, n: number): T[] {
  return [...rows].sort((a, b) => valueField(b) - valueField(a)).slice(0, n);
}

/** Averages a numeric field per distinct key value (nullish values excluded from the average). */
export function groupAverageBy<T>(
  rows: T[],
  keyField: (row: T) => string | null | undefined,
  valueField: (row: T) => number | null | undefined,
): { key: string; average: number }[] {
  const sums = new Map<string, { total: number; count: number }>();
  for (const row of rows) {
    const value = valueField(row);
    if (value == null) continue;
    const key = keyField(row) ?? 'Unknown';
    const entry = sums.get(key) ?? { total: 0, count: 0 };
    entry.total += value;
    entry.count += 1;
    sums.set(key, entry);
  }
  return Array.from(sums.entries())
    .map(([key, { total, count }]) => ({ key, average: total / count }))
    .sort((a, b) => b.average - a.average);
}

export function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}
